import React from 'react'
import { withRouter } from 'next/router'
import Layout from '../../components/Layout/Layout'
import styled from 'styled-components'
import { SCREEN_SIZE } from '../../lib/Media'
import Head from 'next/head'
import { get } from '../../lib/fetch'
import isEmpty from 'lodash/isEmpty'
import PromoList from '../../components/StoreWeeklyAds/PromoList'
import NdsThemeProvider from '@ntuctech/devex-tangram/Theme/NdsThemeProvider'
import PromoLayout from '../../components/StoreWeeklyAds/PromoLayout'
import dynamic from 'next/dynamic'
import { EventTrackingConsumer } from '../../components/EventTrackingProvider/EventTrackingProvider'
import { setCtaLocationMeta } from '../../lib/utils'

const RecipeErrorPage = dynamic(() =>
  import('../../components/Recipe/RecipeErrorPage')
)

const MainContainer = styled.div`
  padding: 0.5rem 1.5rem;
  background-color: #fff;
  ${SCREEN_SIZE.Below.MobileL} {
    padding: 0.5rem 1rem;
  }
`
const FooterSection = styled.div`
  display: flex;
  padding: 3.5rem 0 1rem;
  justify-content: space-evenly;
  ${SCREEN_SIZE.Below.MobileL} {
    flex-direction: column;
  }
`
const StyledSection = styled.div`
  padding: 0 1.5rem 1rem 0;
  h4 {
    line-height: 1.25rem;
    font-weight: 700;
    padding-bottom: 0.75rem;
  }
  p {
    line-height: 1.25rem;
    color: #666;
  }
  a {
    color: #1557bf;
    text-decoration: none;
  }
`

const fetchResponse = async page => {
  /* istanbul ignore next */
  try {
    const response = await get(`v1/swa?page=${page}`)
    if (response?.data?.layouts.length) {
      return {
        promoDetail: response.data,
        statusCode: response.code,
      }
    }
    return { statusCode: response.code }
  } catch (e) {
    return { statusCode: 500 }
  }
}

const getBannerInfo = data => {
  const layouts = data?.layouts || []
  return layouts.find(layout => layout?.collection === 'imageSlideShow')
}

const getPublicationsInfo = data => {
  const layouts = data?.layouts || []
  return layouts.find(layout => layout?.collection === 'publications')
}
class StoreWeeklyAd extends React.Component {
  constructor(props) {
    super(props)
    const { promoDetail, statusCode } = props
    const promoInfo = getPublicationsInfo(promoDetail) || {}
    const bannerInfo = getBannerInfo(promoDetail) || {}
    this.state = {
      promoInfo,
      statusCode,
      bannerInfo,
    }
  }

  componentDidMount() {
    const { query } = this.props.router
    const ctaLocationMeta = query.tag || 'all'
    setCtaLocationMeta([ctaLocationMeta])
  }

  render() {
    const { promoInfo, statusCode, bannerInfo } = this.state
    if (isEmpty(promoInfo?.value)) {
      return (
        <RecipeErrorPage
          categoryList={this.props.categoryList}
          organizationData={this.props.organizationData}
          errorCode={statusCode}
          pathname={this.props.router?.pathname}
        />
      )
    }
    return (
      <>
        <Head>
          <title>Best Weekly Grocery Deals & Savings</title>
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="FairPrice" />
          <meta
            property="og:title"
            content="Best Weekly Grocery Deals & Savings"
          />
          <meta
            name="description"
            key="meta_description"
            content="Discover latest weekly promotions on fresh produce, groceries, household essentials, and many more. Enjoy special offers and exclusive deals at FairPrice!"
          />
        </Head>
        <NdsThemeProvider>
          <Layout
            categoryList={this.props.categoryList}
            organizationData={this.props.organizationData}
            noWrapper="noGap"
          >
            <PromoLayout>
              <MainContainer>
                <PromoList
                  bannerInfo={bannerInfo?.value}
                  items={promoInfo?.value}
                  hasMore={
                    promoInfo?.pagination?.totalPages >
                    promoInfo?.pagination?.page
                  }
                  loadMore={async () => {
                    const apiResponse = await fetchResponse(
                      promoInfo?.pagination?.page + 1
                    )
                    /* istanbul ignore next */
                    if (!isEmpty(apiResponse)) {
                      const nextPageData = getPublicationsInfo(
                        apiResponse.promoDetail
                      )
                      nextPageData.value = [
                        ...(promoInfo?.value || []),
                        ...(nextPageData?.value || []),
                      ]
                      this.setState({
                        promoInfo: nextPageData,
                      })
                    }
                  }}
                />
                <FooterSection>
                  <StyledSection>
                    <h4>NTUC FairPrice Weekly Promotions</h4>
                    <p>
                      Enjoy greater value on your groceries with the latest
                      weekly offers on a wide range of products: fresh fruits,
                      vegetables, meat and seafood; rice, noodles and other
                      cooking ingredients; frozen food; snacks and drinks;
                      household essentials; cleaning products; health and
                      wellness items; beer, wine and spirits; cookware and more!
                    </p>
                    <p>
                      Browse FairPrice’s shoppable store weekly ads to find new
                      deals and promotions. Don’t miss our 1-day, 2-day, 3-day
                      or 4-day special offers! Plus, get updates on the latest
                      Purchase with Purchase promotions to make your grocery
                      shopping more rewarding.
                    </p>
                    <p>
                      {' '}
                      You can hop between each catalogue to view items that are
                      on discount, from{' '}
                      <a href="https://promotions.fairprice.com.sg/price-drop-buy-now-must-buy">
                        Must Buy
                      </a>{' '}
                      deals and{' '}
                      <a href="https://promotions.fairprice.com.sg/price-drop-buy-now-fresh-buys">
                        Fresh Buys
                      </a>{' '}
                      to{' '}
                      <a href="https://promotions.fairprice.com.sg/price-drop-buy-now-weekly-savers">
                        Weekly Savers
                      </a>
                      ,{' '}
                      <a href="https://www.fairprice.com.sg/events/in-store/price-drop-buy-now-weekly-neighbourhood-deals/">
                        Neighbourhood Deals
                      </a>{' '}
                      (4-day offers that start on Thursday) and more! When
                      you’ve found a product you’re interested in, simply select
                      it. You can then save it to your shopping list on the{' '}
                      <a href="https://www.fairprice.com.sg/go-digital-in-stores/">
                        FairPrice Group app{' '}
                      </a>
                      and purchase it from your preferred store, or{' '}
                      <a href="https://www.fairprice.com.sg/">
                        shop online at FairPrice
                      </a>{' '}
                      and have it delivered right to your doorstep.
                    </p>
                  </StyledSection>
                  <StyledSection>
                    <h4>Greater Value Every Day</h4>
                    <p>
                      With FairPrice, you can shop smarter and save more on your
                      groceries and daily necessities! The{' '}
                      <a href="https://promotions.fairprice.com.sg/everyday-low-price">
                        Everyday Low Price
                      </a>{' '}
                      scheme enables you to enjoy a price freeze on 50 selected
                      popular essential items every month, while the{' '}
                      <a href="https://www.fairprice.com.sg/weekly-promotions/">
                        Price Drop Buy Now
                      </a>{' '}
                      scheme offers weekly discounts of up to 50% on selected
                      products.
                    </p>
                    <p>
                      <a href="https://www.fairprice.com.sg/tips/money-saving/enjoy-greater-value-and-savings-with-fairprice/">
                        Stretch your dollar further with our discount schemes
                      </a>
                      ; you can enjoy even more savings daily if you’re an NTUC
                      Union, FairPrice or Link member, or on designated days if
                      you’re a senior citizen (aged 60 and above), a Pioneer or
                      Merdeka Generation cardholder or a CHAS Blue cardholder.
                    </p>
                    <p>
                      NTUC Union and Link members can also enjoy exclusive 50%
                      off deals every week (Union deals). Simply present your
                      Link Rewards card or the FairPrice Group app to enjoy the
                      deals. You can also pay with your NTUC Link Card or{' '}
                      <a href="https://www.fairprice.com.sg/events/online/enjoy-greater-savings-at-fairprice-with-trust/">
                        Trust
                      </a>{' '}
                      Link Card to enjoy the promotions. With the Trust credit
                      card, you save up to 21% on FairPrice Group groceries and
                      food when you hit the minimum spend anywhere outside
                      FairPrice Group. Don’t have a Trust card yet? Sign up with
                      Trust, the digital bank by Standard Chartered and
                      FairPrice Group.
                    </p>
                  </StyledSection>
                  <StyledSection>
                    <h4> About NTUC FairPrice</h4>
                    <p>
                      Founded by the labour movement in 1973, NTUC FairPrice’s
                      social mission is to moderate the cost of living in
                      Singapore. From one supermarket, it has grown to become a
                      leading retailer in Singapore, serving more than half a
                      million shoppers daily through a network of over 370
                      outlets. It comprises{' '}
                      <a href="https://www.fairprice.com.sg/events/in-store/">
                        FairPrice supermarkets
                      </a>{' '}
                      and e-commerce portal,{' '}
                      <a href="https://www.fairprice.com.sg/events/finest/">
                        FairPrice Finest
                      </a>
                      ,{' '}
                      <a href="https://www.fairprice.com.sg/events/xtra/">
                        FairPrice Xtra
                      </a>
                      , FairPrice Shop, Cheers convenience stores, FairPrice
                      Xpress and{' '}
                      <a href="https://unity.com.sg/unity-promotions/">Unity</a>{' '}
                      pharmacy.
                    </p>
                  </StyledSection>
                </FooterSection>
              </MainContainer>
            </PromoLayout>
          </Layout>
        </NdsThemeProvider>
      </>
    )
  }
}

const StoreWeeklyAdWrapper = props => (
  <EventTrackingConsumer>
    {({ track }) => <StoreWeeklyAd {...props} track={track} />}
  </EventTrackingConsumer>
)

StoreWeeklyAdWrapper.getInitialProps = async () => {
  return fetchResponse(1)
}

export default withRouter(StoreWeeklyAdWrapper)
